import { useState, type CSSProperties } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Check, Mail, Sparkles } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Badge, Button } from '@vnetwork/ui/primitives';
import { trackCatalogCta } from '@/lib/analytics-events';
import { useSelfServeActivation } from './activation-mode';
import { CatalogInquiryDialog } from './CatalogInquiryDialog';
import { CatalogActivateDialog } from './CatalogActivateDialog';
import type { ServiceProduct } from './categories';
// Reuse the shared onboarding hero visuals so this generic landing renders
// identically to the service-specific landings (Compute, Kubernetes, Storage,
// CDN): same hero, same bordered feature cards below the CTA.
import { OnboardingCss } from './oss/onboarding-css';
import { CATALOG_PERKS } from './catalog-product-perks';
import {
  heroWrap,
  heroIcon,
  heroEyebrow,
  heroTitle,
  stepList,
  stepRow,
  markerAccent,
  stepTitle,
  stepDesc,
} from './oss/onboarding-styles';

interface CatalogProductLandingProps {
  product: ServiceProduct;
  Icon: LucideIcon;
  statusLabel: string;
  statusTone: 'success' | 'info' | 'warning';
  onBack: () => void;
}

/**
 * Generic catalog landing for products without a dedicated activation landing.
 * Renders the same hero + bordered feature cards as the self-serve landings so
 * every catalog page shares one presentation. The CTA follows the single gate: a
 * staff-impersonation session self-activates a provisionable product (one with an
 * `activateProvider`), otherwise the CTA opens a contact-sales / waitlist inquiry
 * — the default for a direct customer and the only path for a product that has no
 * self-serve provider.
 */
export function CatalogProductLanding({
  product,
  Icon,
  statusLabel,
  statusTone,
  onBack,
}: CatalogProductLandingProps) {
  const coming = product.status === 'coming_soon';
  const perks = CATALOG_PERKS[product.name];
  const selfServeActivation = useSelfServeActivation();
  const canSelfActivate = !!product.activateProvider && selfServeActivation && !coming;
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [activateOpen, setActivateOpen] = useState(false);

  const onPrimary = () => {
    trackCatalogCta(
      product.displayName,
      product.scope,
      canSelfActivate ? 'activate' : coming ? 'waitlist' : 'sales',
    );
    if (canSelfActivate) setActivateOpen(true);
    else setInquiryOpen(true);
  };

  const primaryLabel = canSelfActivate
    ? `Activate ${product.displayName}`
    : coming
      ? 'Join the waitlist'
      : 'Contact sales';

  const headline = coming
    ? `${product.displayName} is coming soon`
    : `Get ${product.displayName} for your organization`;

  return (
    <Container size="xl">
      <PageHeader
        icon={<Icon size={22} />}
        // Match the dedicated landings: only a non-active status shows a badge
        // (Beta / Coming soon) next to the title; "Available" is left implicit.
        title={
          product.status === 'active' ? (
            product.displayName
          ) : (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
              {product.displayName}
              <Badge tone={statusTone} appearance="subtle" size="sm">
                {statusLabel}
              </Badge>
            </span>
          )
        }
        description={product.description}
        actions={
          <Button variant="ghost" onClick={onBack}>
            Back to services
          </Button>
        }
      />

      <div className="vn-ob-hero" style={heroWrap}>
        <OnboardingCss />

        <span className="vn-ob-halo" style={heroIcon} aria-hidden>
          <Icon size={30} />
        </span>

        <p style={heroEyebrow}>{product.displayName}</p>
        <h2 style={heroTitle}>{headline}</h2>

        <Button
          size="lg"
          startIcon={canSelfActivate ? <Sparkles size={16} /> : <Mail size={16} />}
          onClick={onPrimary}
          style={{ marginTop: 'var(--vn-space-2)' }}
        >
          {primaryLabel}
        </Button>

        {perks ? (
          // Rich feature cards (title + description) matching the dedicated
          // landings — a per-product entry in CATALOG_PERKS expands each label.
          <div style={{ ...stepList, marginTop: 'var(--vn-space-5)' }}>
            {perks.map((p) => (
              <div key={p.title} style={stepRow}>
                <span style={markerAccent}>
                  <Check size={16} />
                </span>
                <div style={{ display: 'grid', gap: 2 }}>
                  <p style={stepTitle}>{p.title}</p>
                  <p style={stepDesc}>{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Fallback for a product with no perk copy yet: compact, centered
          // label cards so the hero never shows an empty right-hand gutter.
          <div style={featureGrid}>
            {product.features.map((f) => (
              <div key={f} style={featureCard}>
                <span style={markerAccent}>
                  <Check size={16} />
                </span>
                <p style={{ ...stepTitle, margin: 0 }}>{f}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <CatalogInquiryDialog
        open={inquiryOpen}
        onOpenChange={setInquiryOpen}
        product={product}
        mode={coming ? 'waitlist' : 'sales'}
      />

      {canSelfActivate && (
        <CatalogActivateDialog
          open={activateOpen}
          onOpenChange={setActivateOpen}
          product={product}
        />
      )}
    </Container>
  );
}

const featureGrid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: 'var(--vn-space-3)',
  width: '100%',
  maxWidth: 520,
  marginTop: 'var(--vn-space-5)',
};

const featureCard: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 'var(--vn-space-2)',
  padding: 'var(--vn-space-3) var(--vn-space-4)',
  border: '1px solid var(--vn-border-default)',
  borderRadius: 'var(--vn-radius-lg)',
  background: 'var(--vn-bg-surface)',
};

export default CatalogProductLanding;
